import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import {
  MAX_CAPTION_LENGTH,
  MAX_SRE_PHOTOS,
  SrePhoto,
  addPhoto,
  removePhoto,
  setPhotoCaption,
} from '../../../utilities/sreReport';

/**
 * The photos of an SRE report: a two-wide grid with a caption under each photo and an add tile
 * (hidden once the limit is reached). Adding a photo asks for an optional caption; the pencil on a
 * photo edits it later. When `editable` is false the grid only shows the photos and their captions.
 */
@Component({
  selector: 'app-sre-photo-grid',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, DialogModule],
  templateUrl: './sre-photo-grid.component.html',
  styleUrl: './sre-photo-grid.component.scss',
})
export class SrePhotoGridComponent {
  @Input() photos: SrePhoto[] = [];
  @Input() editable = true;
  @Input() required = false;
  @Input() showError = false;

  @Output() photosChange = new EventEmitter<SrePhoto[]>();

  readonly maxPhotos = MAX_SRE_PHOTOS;
  readonly maxCaptionLength = MAX_CAPTION_LENGTH;
  readonly skipLabel = $localize`:@@sre.caption.skip:Skip`;
  readonly cancelLabel = $localize`:@@cancelButton:Cancel`;
  readonly saveLabel = $localize`:@@saveButton:Save`;

  captionDialogVisible = false;
  captionDraft = '';
  // A photo that has been picked but not added yet, while its caption is asked for.
  private pendingUrl: string | null = null;
  private editingIndex: number | null = null;

  get isNewPhoto(): boolean {
    return this.pendingUrl !== null;
  }

  get canAdd(): boolean {
    return this.editable && this.photos.length < MAX_SRE_PHOTOS;
  }

  onFileChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Let the same file be chosen again later.
    input.value = '';
    if (!file || !file.type.startsWith('image/') || !this.canAdd) {
      return;
    }

    const reader = new FileReader();
    reader.onload = (loaded) => {
      this.pendingUrl = loaded.target?.result as string;
      this.editingIndex = null;
      this.captionDraft = '';
      this.captionDialogVisible = true;
    };
    reader.readAsDataURL(file);
  }

  editCaption(index: number) {
    this.pendingUrl = null;
    this.editingIndex = index;
    this.captionDraft = this.photos[index].caption ?? '';
    this.captionDialogVisible = true;
  }

  remove(index: number) {
    this.photosChange.emit(removePhoto(this.photos, index));
  }

  saveCaption() {
    if (this.pendingUrl !== null) {
      this.photosChange.emit(addPhoto(this.photos, this.pendingUrl, this.captionDraft));
    } else if (this.editingIndex !== null) {
      this.photosChange.emit(setPhotoCaption(this.photos, this.editingIndex, this.captionDraft));
    }
    this.closeCaptionDialog();
  }

  /** Skip when a photo was just added (it is added without a caption); Cancel when editing. */
  skipOrCancelCaption() {
    if (this.pendingUrl !== null) {
      this.photosChange.emit(addPhoto(this.photos, this.pendingUrl, null));
    }
    this.closeCaptionDialog();
  }

  private closeCaptionDialog() {
    this.captionDialogVisible = false;
    this.pendingUrl = null;
    this.editingIndex = null;
    this.captionDraft = '';
  }
}
